/**
 * <ModuleGate module="inventory"> — feature-flag route guard.
 *
 * If the given workspace module is disabled for the current clinic:
 *   - renders a friendly "Module Disabled" page instead of the child route
 *   - never mounts the child component, so no data loading, no API pings,
 *     no analytics tracking, no side effects fire.
 *
 * If enabled, renders {children} untouched (or <Outlet /> when used as a
 * layout wrapper).
 *
 * Companion to backend `require_module` in `routers/workspace.py`. Both
 * read the same `enabled_modules` set so the UX matches the API behaviour
 * exactly.
 */
import { Outlet, Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useWorkspace } from "@/lib/workspace";

export default function ModuleGate({ module, children }) {
  const workspace = useWorkspace();
  const { user } = useAuth();

  // While the workspace is still loading (first paint before the /workspace/
  // modules API responds) we optimistically allow the child to mount using
  // the static DEFAULT_ENABLED set. This prevents a flash of "Module
  // Disabled" on refresh for modules that are actually ON.
  if (workspace.loading) return children ?? <Outlet />;

  if (workspace.isEnabled(module)) return children ?? <Outlet />;

  const meta = (workspace.catalog || []).find((m) => m.id === module) || { label: module, desc: "" };
  const isAdmin = user?.role === "admin";

  return (
    <div className="max-w-xl mx-auto py-16 px-6 text-center" data-testid={`module-disabled-${module}`}>
      <div className="w-14 h-14 mx-auto rounded-2xl bg-linen grid place-items-center">
        <Lock className="w-6 h-6 text-ink-soft" />
      </div>
      <div className="mt-4 overline text-ink-soft">Module disabled</div>
      <h1 className="mt-2 font-display text-3xl tracking-tight">{meta.label}</h1>
      <p className="mt-3 text-sm text-ink-mid">
        {meta.desc || "This module is turned off for your clinic."}
      </p>
      <div className="mt-6 text-xs text-ink-soft leading-relaxed">
        All data is preserved. Re-enabling restores every record, report, dashboard widget and
        automation exactly as it was before.
      </div>
      {isAdmin ? (
        <Link
          to="/app/settings/workspace/modules"
          data-testid="module-disabled-open-manager"
          className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-pine text-linen px-4 py-2 text-sm font-medium hover:bg-pine/90"
        >
          Enable in Module Manager
        </Link>
      ) : (
        <div className="mt-6 rounded-2xl border border-[#E8EAE9] bg-white px-4 py-3 text-xs text-ink-mid">
          Ask your admin to enable it in <strong>Settings → Workspace → Module Manager</strong>.
        </div>
      )}
    </div>
  );
}
